import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import { useFinanceContext } from "../contexts/FinanceContext";
import { formatMonthYearDisplay, formatCurrency } from "../utils";
import { MonthYear } from "@/types/monthYear";
import { FinanceService } from "../services/financeService";
import ChartsContent from "./ChartsContent";
import { TreeRow } from "./TreeRow";
import { categoriesApiV2, sheetsApiV2 } from "@/services/apiV2";
import { Pencil } from "lucide-react";

type Tab = "SPREADSHEET" | "CHARTS";

const MiniPocheteLogo = () => (
  <img
    src="/logos/pochete.svg"
    alt="Pochete.App"
    className="w-20 h-20"
    draggable={false}
  />
);

const MiniPocheteLogoText = () => (
  <img
    src="/logos/pochete-text.svg"
    alt="Pochete.App"
    className="w-22 h-[40px]"
    draggable={false}
  />
);

const MainScreen: React.FC = () => {
  const {
    categories,
    sheets,
    getMonthlyValues,
    updateValue,
    addSheet,
    setCategories,
    monthlyValues,
  } = useFinanceContext();
  const { setIsAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const monthInputRef = useRef<HTMLInputElement | null>(null);

  const [activeTab, setActiveTab] = useState<Tab>("SPREADSHEET");
  const [currentSheetId, setCurrentSheetId] = useState<number>();
  const [currentDate, setCurrentDate] = useState(new Date());
  const monthYear = MonthYear.fromDate(currentDate);

  useEffect(() => {
    if (sheets.length > 0) {
      if (!currentSheetId || !sheets.find((s) => s.id === currentSheetId)) {
        setCurrentSheetId(sheets.find((s) => s.principal)?.id);
      }
    }
  }, [sheets]);

  useEffect(() => {
    categoriesApiV2.getAll(currentSheetId).then((res) => {
      setCategories(res);
    });
    getMonthlyValues(currentSheetId);
  }, [currentSheetId]);

  const handlePrevMonth = () =>
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  const handleNextMonth = () =>
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const my = MonthYear.fromString(val);
    setCurrentDate(new Date(my.year, my.month - 1, 1));
  };

  const openMonthPicker = () => {
    const el = monthInputRef.current;
    if (!el) return;

    // Chromium-based browsers support showPicker() for date/month inputs.
    const anyEl = el as any;
    if (typeof anyEl.showPicker === "function") {
      anyEl.showPicker();
      return;
    }

    // Fallback: focus + click (some browsers will open native picker)
    el.focus();
    el.click();
  };

  const handleAddSheet = () => {
    navigate("/categories");

    return;
    const name = prompt("Digite o nome da nova planilha:");
    if (name) {
      const newId = addSheet(name);
      setCurrentSheetId(newId);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    navigate("/login", { replace: true });
  };

  const getCategoryValue = (catId: string) => {
    return (
      (monthlyValues.find(
        (v) =>
          v.sheet_id === currentSheetId &&
          v.category_id === catId &&
          v.year === monthYear.year &&
          v.month + 1 === monthYear.month
      )?.amount || 0) / 100
    );
  };

  const calculateSum = (catId: string): number => {
    return (
      FinanceService.calculateCategorySum(
        catId,
        categories,
        monthlyValues,
        monthYear,
        currentSheetId
      ) / 100
    );
  };

  const totals = useMemo(() => {
    const incomes = categories
      .filter((c) => !c.parentId && c.type === "income")
      .reduce((acc, c) => acc + calculateSum(c.id), 0);
    const expenses = categories
      .filter((c) => !c.parentId && c.type === "expense")
      .reduce((acc, c) => acc + calculateSum(c.id), 0);
    return { incomes, expenses, balance: incomes - expenses };
  }, [
    categories,
    monthlyValues,
    monthYear.year,
    monthYear.month,
    currentSheetId,
  ]);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-zinc-200">
        {(() => {
          const HeaderActions = () => (
            <>
              {/* <button
                onClick={handleLogout}
                className="px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 font-black text-xs uppercase tracking-widest transition-all"
                title="Sair da Conta"
              >
                Sair
              </button> */}
            </>
          );

          return (
            <>
              {/* Mobile: logo + actions on same row */}
              <div className="flex items-center justify-between gap-3 md:hidden w-full flex justify-center">
                <div className="flex items-center gap-3">
                  <MiniPocheteLogo />
                  <div className="leading-tight">
                    <MiniPocheteLogoText />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <HeaderActions />
                </div>
              </div>

              {/* Desktop: logo at left, actions at right */}
              <div className="hidden md:flex items-center gap-3">
                <div className="">
                  <MiniPocheteLogo />
                </div>
                <div>
                  <h2 className="text-xl font-black text-zinc-900 leading-tight">
                    <MiniPocheteLogoText />
                  </h2>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-3">
                <HeaderActions />
              </div>
            </>
          );
        })()}

        {/* Month Selector */}
        <div className="flex items-center justify-between gap-2 bg-zinc-100 p-1.5 rounded-xl border border-zinc-200">
          <button
            onClick={handlePrevMonth}
            className="p-2 hover:bg-white rounded-lg transition-all text-zinc-500 hover:text-[#3f83f8]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <div className="relative flex items-center group min-w-[160px]">
            <input
              ref={monthInputRef}
              type="month"
              value={monthYear.toString()}
              onChange={handleMonthChange}
              className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
              aria-label="Selecionar mês"
            />
            <div
              role="button"
              tabIndex={0}
              onClick={openMonthPicker}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openMonthPicker();
                }
              }}
              className="px-4 py-1 text-center w-full font-bold text-zinc-800 text-sm group-hover:text-[#3f83f8] transition-colors flex items-center justify-center gap-2 select-none cursor-pointer"
            >
              {formatMonthYearDisplay(monthYear)}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 text-zinc-400 group-hover:text-[#3f83f8]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </div>
          </div>

          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-white rounded-lg transition-all text-zinc-500 hover:text-[#3f83f8]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-50 rounded-bl-full -mr-4 -mt-4 opacity-50"></div>
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] mb-1">
            Receitas
          </span>
          <span className="text-2xl font-black text-emerald-600 tracking-tight">
            {formatCurrency(totals.incomes)}
          </span>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-zinc-200 shadow-sm flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-rose-50 rounded-bl-full -mr-4 -mt-4 opacity-50"></div>
          <span className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] mb-1">
            Despesas
          </span>
          <span className="text-2xl font-black text-rose-600 tracking-tight">
            {formatCurrency(totals.expenses)}
          </span>
        </div>
        <div className="bg-zinc-900 p-5 rounded-2xl border border-zinc-800 shadow-xl flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-white/5 rounded-bl-full -mr-4 -mt-4"></div>
          <span className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em] mb-1">
            Balanço Total
          </span>
          <span
            className={`text-2xl font-black tracking-tight ${
              totals.balance >= 0 ? "text-white" : "text-rose-400"
            }`}
          >
            {formatCurrency(totals.balance)}
          </span>
        </div>
      </div>

      {/* Tab Switcher & Sheet Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex bg-zinc-200/50 p-1 rounded-2xl w-full sm:w-fit border border-zinc-200/60">
          <button
            onClick={() => {
              setActiveTab("SPREADSHEET");
            }}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${
              activeTab === "SPREADSHEET"
                ? "bg-white text-[#3f83f8] shadow-sm"
                : "text-zinc-500 hover:text-zinc-700"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="3" y1="9" x2="21" y2="9"></line>
              <line x1="3" y1="15" x2="21" y2="15"></line>
              <line x1="9" y1="3" x2="9" y2="21"></line>
              <line x1="15" y1="3" x2="15" y2="21"></line>
            </svg>
            Planilha
          </button>
          <button
            onClick={() => {
              /*setActiveTab("CHARTS"); */ alert("Em breve!");
            }}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${
              activeTab === "CHARTS"
                ? "bg-white text-[#3f83f8] shadow-sm"
                : "text-zinc-500 hover:text-zinc-700"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
            Gráficos
          </button>
        </div>

        {activeTab === "SPREADSHEET" && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <select
                value={currentSheetId}
                onChange={(e) => {
                  setCurrentSheetId(Number(e.target.value));
                }}
                className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-sm font-bold text-zinc-700 appearance-none outline-none focus:ring-2 focus:ring-[#3f83f8] transition-all"
              >
                {sheets.length > 0 &&
                  sheets.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
            <button
              onClick={handleAddSheet}
              className="p-2.5 bg-[#3f83f8] hover:bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-50 transition-all active:scale-95"
              title="Nova Planilha"
            >
              <Pencil className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Conditional Content */}
      <div className="animate-in fade-in duration-500">
        {activeTab === "SPREADSHEET" ? (
          <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm overflow-hidden">
            <div className="grid grid-cols-[1fr,150px] md:grid-cols-[1fr,200px] border-b border-zinc-100 bg-zinc-50/50 font-bold text-zinc-400 text-[10px] tracking-widest uppercase">
              <div className="p-4 pl-8">Estrutura de Categorias</div>
              <div className="p-4 text-right pr-8">Montante Mensal</div>
            </div>

            <div className="divide-y divide-zinc-50 overflow-x-auto">
              <div
                className={`grid grid-cols-[1fr,150px] md:grid-cols-[1fr,200px] hover:bg-zinc-50/80 transition-all group bg-zinc-50/20`}
              >
                <div className="p-4 pl-8">Receitas</div>
              </div>
              {categories
                .filter((c) => !c.parentId && c.type == "income")
                .map((root) => (
                  <TreeRow
                    key={root.id}
                    category={root}
                    allCategories={categories}
                    monthYear={monthYear}
                    onUpdate={(category, my, val) =>
                      updateValue(currentSheetId, category, my, val)
                    }
                    monthlyValues={monthlyValues.filter(
                      (v) => v.sheet_id === currentSheetId
                    )}
                    level={0}
                    calculateSum={calculateSum}
                    getCategoryValue={getCategoryValue}
                  />
                ))}
              <div
                className={`grid grid-cols-[1fr,150px] md:grid-cols-[1fr,200px] hover:bg-zinc-50/80 transition-all group bg-zinc-50/20`}
              >
                <div className="p-4 pl-8">Despesas</div>
              </div>
              {categories
                .filter((c) => !c.parentId && c.type == "expense")
                .map((root) => (
                  <TreeRow
                    key={root.id}
                    category={root}
                    allCategories={categories}
                    monthYear={monthYear}
                    onUpdate={(category, my, val) =>
                      updateValue(currentSheetId, root, my, val)
                    }
                    monthlyValues={monthlyValues.filter(
                      (v) => v.sheet_id === currentSheetId
                    )}
                    level={0}
                    calculateSum={calculateSum}
                    getCategoryValue={getCategoryValue}
                  />
                ))}
              {categories.length === 0 && (
                <div className="p-20 text-center">
                  <div className="bg-zinc-50 inline-block p-4 rounded-full mb-4">
                    <MiniPocheteLogo />
                  </div>
                  <p className="text-zinc-400 font-medium">
                    Nenhuma categoria encontrada no seu bolso.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <ChartsContent
            categories={categories}
            monthlyValues={monthlyValues.filter(
              (v) => v.sheet_id === currentSheetId
            )}
          />
        )}
      </div>
    </div>
  );
};

export default MainScreen;
